import React, { useEffect, useState } from 'react';
import { noop } from 'lodash';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';

/**
 * SimpleSelect component - used for filters selection and some form fields selection.
 *
 * @param {Object} props - Props for the SimpleSelect component.
 * @param {Array} props.options - The options for the select input.
 * @param {setState} props.onChange - The function to be called when the value of the select input changes.
 * @param {boolean} props.clear - Flag indicating whether to clear the select input.
 * @param {Object} [props] - Additional props to be spread on the Select component.
 * @returns {JSX.Element} The SimpleSelect component.
 * @example
 *  <SimpleSelect 
 *      options={['option1', 'option2']} 
 *      onChange={setOption} 
 *      clear={true} 
 *      // ... 
 *  />
 * 
 */
function SimpleSelect({ options, onChange, clear, ...props}) {
    const [value, setValue] = useState([]);

    const { t } = useTranslation()

    /**
     * Return a list of options with value and label. It can receive an array of strings or an array with objects in which the value is 
     * different from the label.
     * 
     * @returns {Array<{value: string, label: string}>} The list of options
     */
    const optionList = options.map((option) => {
        return {
            value: option?.value ?? option,
            label: option?.label ?? option
        }
    });

    useEffect(() => {
        /**
         * - If `clear` is `true`, set filters to none.
         * - If not, change value, triggering a new filter.
         */
        if (clear) { 
            onChange([]);
            setValue([]);
        } else {
            onChange(value)
        }
    }, [value, clear, onChange]);

    useEffect(() => {
        /**
         * Sort options given.
         */
        options.sort()
    }, [options])

    return (
        <Select
            mode='multiple'
            placeholder={t('selectFilter')}
            optionLabelProp='label'
            allowClear
            showSearch
            value={clear ? [] : value}
            onChange={setValue}
            options={optionList}
            {...props}
        >
        </Select>
    );
}

SimpleSelect.defaultProps = {
    options: [],
    onChange: noop,
    clear: false
};

export default SimpleSelect;